import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { CarryOutOutlined } from '@ant-design/icons';
import { Tree } from 'antd';
import { useLocation } from "react-router-dom";
import { useAuth } from "../../providers/AuthProvider";
import SidebarComponent from "../../components/SidebarComponent";
import { Check, ArrowRight } from 'lucide-react';
import { getStepMinutes, isSectionConfirmed } from './components/applicationProgress';

/**
 * Neutral status icon for the application section list
 * (done / current / needs confirm / not started).
 */
const SectionStatusIcon = ({ status, size = 'md' }) => {
    const box = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4';
    if (status === 'done') {
        return (
            <div className={`flex ${box} shrink-0 items-center justify-center rounded-full bg-success text-white`}>
                <Check className="h-2.5 w-2.5" strokeWidth={3.5} />
            </div>
        );
    }
    if (status === 'current') {
        return (
            <div className={`flex ${box} shrink-0 items-center justify-center rounded-full border-2 border-secondary`}>
                <div className="h-1.5 w-1.5 rounded-full bg-secondary" />
            </div>
        );
    }
    if (status === 'confirm') {
        return <div className={`${box} shrink-0 rounded-full border-2 border-amber-400 bg-amber-50`} />;
    }
    return <div className={`${box} shrink-0 rounded-full border-2 border-gray-300`} />;
};

import { createBreakpoint } from "react-use";

const useBreakpoint = createBreakpoint({ tablet: 768, mobile: 0 });

const ApplicationSideNavBar = ({ steps, currentStep, disabled, onStepNavigate, currentStepIndex, ...props }) => {
    const { auth } = useAuth();

    const breakpoint = useBreakpoint();
    const location = useLocation()
    const talentUser = props.user || auth;
    const isAdminEditing = auth?.canManageClientResourceTalent() && !!props.user;

    // Display-only: is the (pre-approval) application ready to review & submit?
    const reviewStep = TB.find(steps || [], step => step?.path === '/review');
    const applicationReady = !talentUser?.isApplicationApproved?.()
        && !talentUser?.didSubmitApplication?.()
        && !!talentUser?.hasCompletedApplication?.()
        && TB.every(steps || [], step => !step?.StepInfo || !step.NeedsCompletion?.(talentUser) || isSectionConfirmed(talentUser, step));

    const talentSteps = useMemo(() => {

        if (!talentUser.isApplicationApproved()) {
            return steps
        }

        return TB.groupBy(steps, step => step.StepSection)
    }, [talentUser?.isApplicationApproved(), steps]);

    const sideBarTreeMenu = useMemo(() => {
        if (TB.isArray(talentSteps)) {
            return []
        }
        let menu = []
        TB.map(talentSteps, (sectionSteps, sectionKey) => {

            menu.push({
                title: sectionKey,
                key: `${TB.slugify(sectionKey)}`,
                icon: <CarryOutOutlined />,
                children: TB.map(sectionSteps, (step, index) => {
                    if (!step.StepTitle) return null;

                    let needsCompletion = step.NeedsCompletion(talentUser);

                    let isActive = currentStep.StepTitle === step.StepTitle;
                    let isCompleted =
                        (!needsCompletion && index <= currentStepIndex) ||
                        step.hasCompleted(talentUser || {});


                    return {
                        title: (
                            <div
                                className={TB.mapClass('flex flex-row items-center sub-step-title step-title py-0 m-0 h-7', {
                                    isApproved: talentUser.isApplicationApproved(),
                                    isCompleted,
                                    isActive,
                                    'no-click': disabled
                                })}>
                                <i className={TB.mapClass("", {
                                    "fas fa-check-circle": needsCompletion && isCompleted,
                                    "fas fa-times-circle": needsCompletion && !isCompleted,
                                    "fas fa-arrow-alt-circle-right": !needsCompletion,
                                })} />

                                {step.StepTitle}
                            </div>
                        ),
                        key: `${TB.slugify(step.StepTitle)}`,
                    }
                })
            })
        })
        return menu
    }, [location?.pathname, talentSteps, talentUser, disabled]);
    if (!talentUser) return <div className="talent-app-nav lg:w-3/12 p-0" />


    return (

        <SidebarComponent canClose={breakpoint === 'mobile'} maxWidth="18rem" defaultClosed={false}
            openIcon={<i className="fas fa-caret-right" />}>
            <div className={TB.mapClass("talent-app-nav p-0 md:max-w-[280px]", {})}>

                <h3 className={TB.mapClass('font-bold', {
                    'px-2': talentUser.isApplicationApproved(),
                    '!mx-0 !max-w-none !mt-4 !mb-2 px-5 !text-[11px] uppercase tracking-wider text-gray-500': !talentUser.isApplicationApproved(),
                })}>
                    {!talentUser.isApplicationApproved() ?
                        'Application sections' :
                        'Edit Your Profile'
                    }
                </h3>


                {TB.isArray(talentSteps) ? (
                  <React.Fragment>
                    {applicationReady && reviewStep && currentStep !== reviewStep && (
                        <div className="mx-3 mb-3 rounded-lg border border-success/40 bg-success/10 p-3">
                            <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                                <div className="flex h-4 w-4 items-center justify-center rounded-full bg-success text-white">
                                    <Check className="h-2.5 w-2.5" strokeWidth={3.5} />
                                </div>
                                Application ready
                            </div>
                            <p className="mt-1 mb-2.5 text-xs text-gray-600">
                                Every required section is done. Review it once and submit.
                            </p>
                            <button
                                type="button"
                                disabled={disabled}
                                onClick={() => onStepNavigate(reviewStep)}
                                className="btn btn-primary btn-block inline-flex items-center justify-center gap-1.5 !text-xs !py-2"
                            >
                                Review &amp; submit <ArrowRight className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    )}

                    {talentSteps.map((step, index) => {
                        if (!step.StepTitle) return null;

                        const isActive = currentStep.StepTitle === step.StepTitle;
                        const isPrevious = index <= currentStepIndex;
                        const isNext = index > currentStepIndex;

                        let needsCompletion = step.NeedsCompletion(talentUser);

                        const isCompleted = (!needsCompletion && isPrevious) ||
                            step.hasCompleted(talentUser || {});
                        const canGoToNext = isPrevious || (isCompleted && index === (currentStepIndex + 1));

                        // Display-only status (navigation rules above are unchanged)
                        const needsConfirm = needsCompletion && isCompleted && !isSectionConfirmed(talentUser, step);
                        const status = isActive ? 'current'
                            : needsConfirm ? 'confirm'
                            : (needsCompletion && isCompleted) || (!needsCompletion && isCompleted && step.hasCompleted(talentUser || {})) ? 'done'
                            : 'todo';
                        const isOptional = !needsCompletion && step.StepInfo;

                        return (
                            <button
                                type="button"
                                key={`step-nav-${index}`}
                                className={TB.mapClass('step-title !h-9 !normal-case !text-[13px] gap-2.5 !pl-5 !pr-4 w-full text-left', {
                                    isApproved: talentUser.isApplicationApproved(),
                                    isPrevious,
                                    isNext,
                                    canGoToNext,
                                    isCompleted,
                                    isActive,
                                    'no-click': disabled
                                })}
                                onClick={() => onStepNavigate(step)}
                            >
                                <SectionStatusIcon status={status} />

                                <div className="grow shrink basis-0 min-w-0 truncate text-gray-900">
                                    {step.StepTitle}
                                </div>

                                {status === 'confirm' && (
                                    <div className="text-[10px] font-semibold text-amber-600">Confirm</div>
                                )}
                                {status !== 'done' && isOptional && (
                                    <div className="text-[10px] text-gray-400">Optional</div>
                                )}
                                {status !== 'confirm' && needsCompletion && (
                                    <div className="text-[10px] text-gray-400 tabular-nums">~{getStepMinutes(step)} min</div>
                                )}
                            </button>
                        )

                    })}
                  </React.Fragment>
                ) : (
                    <React.Fragment>
                        <div className="pl-5">
                            <Tree
                                showLine={true}
                                showIcon={false}
                                defaultExpandedKeys={TB.map(sideBarTreeMenu, key => TB.slugify(key.key))}
                                onSelect={(selectedKeys, info) => {
                                    let key = TB.first(selectedKeys);
                                    if (!key) return;
                                    // console.log('onSelect', key, info)
                                    let step = TB.find(TB.flatMap(TB.values(talentSteps)), step => TB.slugify(step.StepTitle) === key);
                                    if (step) {
                                        // console.log('onstepnav', step)
                                        return onStepNavigate(step)
                                    }
                                }}
                                treeData={TB.map(talentSteps, (sectionSteps, sectionKey) => {
                                    return {
                                        title: sectionKey,
                                        key: `${TB.slugify(sectionKey)}`,
                                        icon: <CarryOutOutlined />,
                                        children: TB.map(sectionSteps, (step, index) => {
                                            if (!step.StepTitle) return null;

                                            let needsCompletion = step.NeedsCompletion(talentUser);

                                            let isActive = currentStep.StepTitle === step.StepTitle;
                                            let isCompleted =
                                                (!needsCompletion && index <= currentStepIndex) ||
                                                step.hasCompleted(talentUser || {});


                                            if (isActive) {
                                                // console.log(step.StepTitle, isCompleted, talentUser.validateWorkPreferences());
                                            }
                                            return {
                                                title: (
                                                    <div
                                                        className={TB.mapClass('profile-tree-link flex flex-row items-center sub-step-title step-title py-0 m-0 h-7', {
                                                            isApproved: talentUser.isApplicationApproved(),
                                                            isCompleted,
                                                            isActive,
                                                            'no-click': disabled
                                                        })}>
                                                        <div className="mr-2">
                                                            <SectionStatusIcon
                                                                size="sm"
                                                                status={isActive ? 'current'
                                                                    : (needsCompletion && isCompleted && !isSectionConfirmed(talentUser, step)) ? 'confirm'
                                                                    : (needsCompletion && isCompleted) ? 'done'
                                                                    : 'todo'}
                                                            />
                                                        </div>

                                                        <div>{step.StepTitle}</div>
                                                        {!needsCompletion && (
                                                            <div className="ml-1.5 text-[10px] font-normal text-gray-400">Optional</div>
                                                        )}
                                                    </div>
                                                ),
                                                key: `${TB.slugify(step.StepTitle)}`,
                                            }
                                        })
                                    }
                                })}
                            />
                        </div>
                    </React.Fragment>
                )}

            </div>
        </SidebarComponent>
    )
};

ApplicationSideNavBar.propTypes = {
    steps: PropTypes.any,
    currentStep: PropTypes.any,
    disabled: PropTypes.any,
    onStepNavigate: PropTypes.any,
};

export default ApplicationSideNavBar;
